import React from "react";
import { Create } from "react-admin";
import { Box, Stack, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import EventBus from "../../EventBus";
import { EventBusEventsEnum } from "../../enums/EventBusEventsEnum";
import { CustomSnackbarTypes } from "../Common/CustomSnackBar";
import CampaignPageService from "../../services/CampaignPageService";
import { CampaignPageServiceEnum } from "../../interfaces/CampaignPage/CampaignPage";
import CampaignPageFormFields, {
    CampaignPageFormValue,
    validateCampaignPageForm,
} from "./CampaignPageFormFields";

const campaignPageService = new CampaignPageService();

const initialForm: CampaignPageFormValue = {
    slug: "",
    campaignName: "",
    service: CampaignPageServiceEnum.CHEFIT,
    metaTitle: "",
    metaDescription: "",
    noindex: false,
    bannerHeading: "",
    bannerDescription: "",
    bannerImageUrl: "",
    isActive: true,
};

const CreateCampaignPage = () => {
    const navigate = useNavigate();
    const [form, setForm] = React.useState<CampaignPageFormValue>(initialForm);
    const [saving, setSaving] = React.useState<boolean>(false);

    const update = (key: keyof CampaignPageFormValue, value: any) => {
        setForm((prev) => ({ ...prev, [key]: value }));
    };

    const handleSubmit = async () => {
        const invalid = validateCampaignPageForm(form);
        if (invalid) {
            EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, {
                message: invalid,
                type: CustomSnackbarTypes.ERROR,
            });
            return;
        }

        setSaving(true);
        try {
            const res = await campaignPageService.createCampaignPage({
                ...form,
                campaignName: form.campaignName.trim(),
                metaTitle: form.metaTitle.trim(),
                metaDescription: form.metaDescription.trim(),
                bannerHeading: form.bannerHeading.trim(),
                bannerDescription: form.bannerDescription.trim(),
                bannerImageUrl: form.bannerImageUrl.trim(),
            });
            EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, {
                message: "Campaign page created",
                type: CustomSnackbarTypes.SUCCESS,
            });
            navigate(res?.data?.id ? `/CampaignPages/${res.data.id}/show` : "/CampaignPages", { replace: true });
        } catch (err) {
            // HttpClient already shows the backend's message (e.g. a slug that is taken).
        } finally {
            setSaving(false);
        }
    };

    return (
        <Create title="Create Campaign Page" resource="CampaignPages">
            <Box sx={{ p: { xs: 1.5, sm: 2.5 } }}>
                <Stack sx={{ mb: 2 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        New Campaign Page
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        A new page at thechefkart.com/campaign/&lt;slug&gt; with its own banner and SEO, sharing the standard campaign body.
                    </Typography>
                </Stack>
                <CampaignPageFormFields
                    mode="create"
                    value={form}
                    onChange={update}
                    onSubmit={handleSubmit}
                    onCancel={() => navigate("/CampaignPages")}
                    submitting={saving}
                />
            </Box>
        </Create>
    );
};

export default CreateCampaignPage;
